import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
  collection,
  collectionGroup,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  startAfter,
  where,
} from "firebase/firestore";
import { firestoreDev, firestoreProd } from "../../firebase";
import {
  FaBuilding,
  FaDatabase,
  FaDownload,
  FaFileCsv,
  FaSpinner,
  FaTimes,
} from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useDarkMode } from "../../components/Theme/useDarkMode";

// ── Constants ─────────────────────────────────────────────────────────────────

const ORG_RESULT_LIMIT = 20;
const HISTORY_PAGE_SIZE = 1000;

const CSV_HEADERS = [
  "organizationId",
  "timestamp",
  "loanId",
  "loanNumber",
  "eventType",
  "fromStage",
  "toStage",
];

// ── Helpers ───────────────────────────────────────────────────────────────────

const getOrgLabel = (org) =>
  String(
    org?.name ||
      org?.organizationName ||
      org?.companyName ||
      org?.orgName ||
      org?.id ||
      "Unknown organization"
  );

const normalizeOrganization = (id, data = {}) => ({
  id,
  name: getOrgLabel({ id, ...data }),
});

const searchOrganizationOptions = (organizations, searchTerm) => {
  const q = searchTerm.trim().toLowerCase();
  if (!q) return organizations.slice(0, ORG_RESULT_LIMIT);
  return organizations
    .filter((org) => {
      const name = getOrgLabel(org).toLowerCase();
      return name.includes(q) || org.id.toLowerCase().includes(q);
    })
    .slice(0, ORG_RESULT_LIMIT);
};

function escapeCsvValue(value) {
  if (value == null) return "";
  const str = String(value);
  if (str.includes(",") || str.includes('"') || str.includes("\n") || str.includes("\r")) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

function rowToCsvLine(row) {
  return row.map(escapeCsvValue).join(",");
}

function triggerDownload(url, filename) {
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
}

// ── Component ─────────────────────────────────────────────────────────────────

const LoanStageAnalyticsPage = ({ onReady }) => {
  const [theme] = useDarkMode();
  const isDark = theme === "dark";

  const history = useHistory();
  const location = useLocation();

  const orgIdFromUrl = useMemo(
    () => new URLSearchParams(location.search).get("org") || null,
    [location.search]
  );

  const [environment, setEnvironment] = useState("prod");
  const [organizationCache, setOrganizationCache] = useState([]);
  const [organizationSearchQuery, setOrganizationSearchQuery] = useState("");
  const [organizationResults, setOrganizationResults] = useState([]);
  const [hasLoadedOrganizations, setHasLoadedOrganizations] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState({
    phase: "",
    eventsRead: 0,
    loansProcessed: 0,
    totalLoans: null,
  });
  const [result, setResult] = useState(null); // { blobUrl, rowCount, filename }

  const scanRef = useRef(null);
  const isMountedRef = useRef(false);

  // Revoke the Blob URL whenever result changes or the component unmounts so
  // the browser can free the backing memory.
  useEffect(() => {
    return () => { if (result?.blobUrl) URL.revokeObjectURL(result.blobUrl); };
  }, [result]);

  const firestore = useMemo(
    () => (environment === "prod" ? firestoreProd : firestoreDev),
    [environment]
  );

  const selectedOrganization = useMemo(
    () => (orgIdFromUrl ? (organizationCache.find((o) => o.id === orgIdFromUrl) ?? null) : null),
    [orgIdFromUrl, organizationCache]
  );

  // ── Styles ──

  const ACCENT = "#7F7FD5";
  const dividerColor = isDark ? "#2d3148" : "#e2e8f0";
  const mutedTextColor = "#888";

  const cardStyle = {
    background: isDark ? "#161b2e" : "#fff",
    border: `1px solid ${dividerColor}`,
    borderRadius: "12px",
    padding: "24px",
    marginBottom: "24px",
    boxShadow: isDark ? "0 4px 24px rgba(0,0,0,0.3)" : "0 2px 12px rgba(0,0,0,0.06)",
  };

  const inputStyle = {
    padding: "10px 14px",
    fontSize: "14px",
    border: `1.5px solid ${isDark ? "#3d4466" : "#cbd5e0"}`,
    borderRadius: "8px",
    background: isDark ? "#1e2130" : "#f7f9fc",
    color: isDark ? "#e2e8f0" : "#1a202c",
    outline: "none",
  };

  const btnStyle = (variant = "primary") => ({
    padding: "10px 20px",
    fontSize: "14px",
    fontWeight: 600,
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    whiteSpace: "nowrap",
    ...(variant === "primary" && { background: ACCENT, color: "#fff" }),
    ...(variant === "danger" && { background: "#e53e3e", color: "#fff" }),
    ...(variant === "success" && { background: "#38a169", color: "#fff" }),
  });

  const envToggleStyle = (env) => ({
    padding: "7px 14px",
    fontSize: "13px",
    fontWeight: 600,
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    background: environment === env ? ACCENT : isDark ? "#2d3148" : "#e2e8f0",
    color: environment === env ? "#fff" : isDark ? "#a0aec0" : "#4a5568",
  });

  // ── Org loading ──

  const loadOrganizations = useCallback(async () => {
    if (!firestore) return [];
    const snap = await getDocs(query(collection(firestore, "organizations"), limit(1000)));
    const orgs = snap.docs
      .map((s) => normalizeOrganization(s.id, s.data()))
      .sort((a, b) => getOrgLabel(a).localeCompare(getOrgLabel(b)));
    setOrganizationCache(orgs);
    setHasLoadedOrganizations(true);
    return orgs;
  }, [firestore]);

  useEffect(() => { onReady?.(); }, [onReady]);

  useEffect(() => {
    let cancelled = false;
    loadOrganizations()
      .then((orgs) => { if (!cancelled) setOrganizationResults(searchOrganizationOptions(orgs, "")); })
      .catch(console.error);
    return () => { cancelled = true; };
  }, [loadOrganizations]);

  useEffect(() => {
    if (!isMountedRef.current) { isMountedRef.current = true; return; }
    const params = new URLSearchParams(location.search);
    params.delete("org");
    history.replace({ search: params.toString() });
    setResult(null);
    setOrganizationSearchQuery("");
    setOrganizationResults(searchOrganizationOptions(organizationCache, ""));
  }, [environment]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { setResult(null); }, [orgIdFromUrl]);

  const selectOrganization = useCallback(
    (org) => {
      const params = new URLSearchParams(location.search);
      params.set("org", org.id);
      history.push({ search: params.toString() });
    },
    [history, location.search]
  );

  const clearSelectedOrganization = useCallback(() => {
    const params = new URLSearchParams(location.search);
    params.delete("org");
    history.push({ search: params.toString() });
    setOrganizationResults(searchOrganizationOptions(organizationCache, organizationSearchQuery));
  }, [history, location.search, organizationCache, organizationSearchQuery]);

  // ── Export logic ──

  const runExport = useCallback(async () => {
    if (!firestore || !selectedOrganization) return;

    const scanId = Date.now();
    scanRef.current = scanId;

    setIsLoading(true);
    setResult(null);
    setProgress({ phase: "Reading history events…", eventsRead: 0, loansProcessed: 0, totalLoans: null });

    try {
      const orgId = selectedOrganization.id;

      // Stream history events filtered by organizationId and type directly — no per-loan grouping needed.
      // Loan number fetches (loans-thin) run concurrently: a getDoc is fired the first time each
      // loanId is encountered so both phases overlap.
      // Requires a Firestore collection group index on (organizationId, type).
      const events = [];                 // flat list of trimmed event objects
      const loanNumberPromises = {};    // loanId → Promise<string>
      let eventsRead = 0;
      let cursor = null;

      const baseQuery = query(
        collectionGroup(firestore, "history"),
        where("organizationId", "==", orgId),
        where("type", "in", ["loan_created", "loan_stage_changed"]),
        limit(HISTORY_PAGE_SIZE)
      );

      while (scanRef.current === scanId) {
        const pageQuery = cursor
          ? query(baseQuery, startAfter(cursor))
          : baseQuery;

        const snap = await getDocs(pageQuery);
        if (scanRef.current !== scanId) break;

        for (const docSnap of snap.docs) {
          const event = docSnap.data();
          const loanId = event.loanId || docSnap.ref.path.split("/")[3];

          if (!loanNumberPromises[loanId]) {
            loanNumberPromises[loanId] = getDoc(
              doc(firestore, "organizations", orgId, "loans-thin", loanId)
            )
              .then((s) => (s.exists() ? (s.data()?.number || s.data()?.loanNumber || "") : ""))
              .catch(() => "");
          }

          events.push({
            loanId,
            type: event.type,
            createdAt: event.createdAt,
            previousStage: event.previousStage,
            newStage: event.newStage,
          });
          eventsRead++;
        }

        const currentEventsRead = eventsRead;
        setProgress((p) => ({ ...p, eventsRead: currentEventsRead }));
        if (snap.docs.length < HISTORY_PAGE_SIZE) break;
        cursor = snap.docs[snap.docs.length - 1];
      }

      if (scanRef.current !== scanId) return;

      setProgress((p) => ({ ...p, phase: "Finalizing…" }));
      const loanNumberMap = {};
      await Promise.all(
        Object.entries(loanNumberPromises).map(async ([loanId, promise]) => {
          loanNumberMap[loanId] = await promise;
        })
      );

      if (scanRef.current !== scanId) return;

      events.sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));

      const rows = events.map((event) => {
        const timestamp = event.createdAt ? new Date(event.createdAt).toISOString() : "";
        const isStageChange = event.type === "loan_stage_changed";
        return [
          orgId,
          timestamp,
          event.loanId,
          loanNumberMap[event.loanId] ?? "",
          event.type,
          isStageChange ? (event.previousStage ?? "") : "",
          isStageChange ? (event.newStage ?? "") : "",
        ];
      });

      // Build the CSV string and hand it off to a Blob immediately.
      // The string itself goes out of scope right after the Blob is created,
      // leaving only the Blob (managed by the browser) and its object URL in memory.
      const csvLines = [CSV_HEADERS.join(","), ...rows.map(rowToCsvLine)];
      const csvBlob = new Blob([csvLines.join("\r\n")], { type: "text/csv;charset=utf-8;" });
      const blobUrl = URL.createObjectURL(csvBlob);

      const orgName = getOrgLabel(selectedOrganization).replace(/[^a-z0-9]/gi, "_").toLowerCase();
      const dateSuffix = new Date().toISOString().slice(0, 10);
      const filename = `loan_stage_history_${orgName}_${dateSuffix}.csv`;

      setResult({ blobUrl, rowCount: rows.length, filename });
    } catch (err) {
      console.error("Export failed:", err);
      toast.error("Export failed. Check the console for details.");
    } finally {
      if (scanRef.current === scanId) setIsLoading(false);
    }
  }, [firestore, selectedOrganization]);

  // ── Render ──

  const orgSelectorSection = (
    <div style={cardStyle}>
      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "16px", flexWrap: "wrap" }}>
        <FaDatabase style={{ color: mutedTextColor, fontSize: "13px" }} />
        <span style={{ fontSize: "13px", color: mutedTextColor }}>Environment:</span>
        <button type="button" style={envToggleStyle("prod")} onClick={() => setEnvironment("prod")}>
          Production
        </button>
        <button type="button" style={envToggleStyle("dev")} onClick={() => setEnvironment("dev")}>
          Development
        </button>
      </div>

      {!selectedOrganization ? (
        <>
          <div style={{ position: "relative" }}>
            <FaBuilding
              style={{
                position: "absolute",
                left: 13,
                top: "50%",
                transform: "translateY(-50%)",
                color: mutedTextColor,
                fontSize: "12px",
                pointerEvents: "none",
              }}
            />
            <input
              type="text"
              value={organizationSearchQuery}
              onChange={(e) => {
                setOrganizationSearchQuery(e.target.value);
                setOrganizationResults(searchOrganizationOptions(organizationCache, e.target.value));
              }}
              placeholder="Search organization by name or ID"
              style={{ ...inputStyle, paddingLeft: 36, width: "100%", boxSizing: "border-box" }}
            />
          </div>

          {organizationResults.length > 0 && (
            <div style={{ marginTop: 14, display: "grid", gap: 8 }}>
              <div style={{ fontSize: 12, color: mutedTextColor, marginBottom: 2 }}>
                {organizationResults.length} organization{organizationResults.length !== 1 ? "s" : ""}
                {!hasLoadedOrganizations ? " found so far" : ""} — select one to continue
              </div>
              {organizationResults.map((org) => (
                <button
                  key={org.id}
                  type="button"
                  onClick={() => selectOrganization(org)}
                  style={{
                    textAlign: "left",
                    padding: "10px 14px",
                    borderRadius: 8,
                    border: `1px solid ${dividerColor}`,
                    background: isDark ? "#1e2130" : "#fff",
                    color: "inherit",
                    cursor: "pointer",
                  }}
                >
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{getOrgLabel(org)}</div>
                  <div style={{ fontSize: 12, color: mutedTextColor, marginTop: 2 }}>{org.id}</div>
                </button>
              ))}
            </div>
          )}
        </>
      ) : (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "12px 14px",
            borderRadius: 10,
            background: isDark ? "#1e2130" : "#f7f9fc",
            border: `1px solid ${dividerColor}`,
          }}
        >
          <FaBuilding style={{ color: ACCENT }} />
          <div>
            <div style={{ fontWeight: 700 }}>{getOrgLabel(selectedOrganization)}</div>
            <div style={{ fontSize: 12, color: mutedTextColor }}>{selectedOrganization.id}</div>
          </div>
          <button
            type="button"
            onClick={clearSelectedOrganization}
            style={{
              marginLeft: "auto",
              padding: "7px 12px",
              borderRadius: 8,
              border: `1px solid ${isDark ? "#3d4466" : "#cbd5e0"}`,
              background: "transparent",
              color: isDark ? "#e2e8f0" : "#1a202c",
              cursor: "pointer",
              fontSize: 13,
            }}
          >
            Change organization
          </button>
        </div>
      )}
    </div>
  );

  const exportSection = selectedOrganization && (
    <div style={cardStyle}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        {isLoading ? (
          <button
            type="button"
            onClick={() => { scanRef.current = null; setIsLoading(false); }}
            style={btnStyle("danger")}
          >
            <FaTimes /> Stop
          </button>
        ) : (
          <button type="button" onClick={runExport} style={btnStyle("primary")}>
            <FaFileCsv /> {result ? "Re-generate CSV" : "Generate CSV"}
          </button>
        )}

        {isLoading && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, color: ACCENT, fontSize: 13 }}>
            <FaSpinner style={{ animation: "spin 1s linear infinite" }} />
            <span>
              {progress.phase}
              {progress.phase.startsWith("Reading") && progress.eventsRead > 0 &&
                ` · ${progress.eventsRead.toLocaleString()} events`}
            </span>
          </div>
        )}

        {result && !isLoading && (
          <>
            <div style={{ fontSize: 13, color: mutedTextColor }}>
              <strong style={{ color: isDark ? "#e2e8f0" : "#1a202c" }}>
                {result.rowCount.toLocaleString()}
              </strong>{" "}
              events ready
            </div>
            <button
              type="button"
              onClick={() => triggerDownload(result.blobUrl, result.filename)}
              style={btnStyle("success")}
            >
              <FaDownload /> Download CSV
            </button>
          </>
        )}
      </div>

      <div style={{ marginTop: 16, fontSize: 12, color: mutedTextColor }}>
        Exports <code style={{ fontSize: 11 }}>loan_created</code> and{" "}
        <code style={{ fontSize: 11 }}>loan_stage_changed</code> events.
        Columns: {CSV_HEADERS.join(", ")}.
      </div>
    </div>
  );

  return (
    <div
      className="container-fluid LoanStageAnalyticsPageContainer"
      style={{
        color: isDark ? "#e2e8f0" : "#1a202c",
        fontFamily: "'Inter', 'Segoe UI', sans-serif",
        minHeight: "100vh",
      }}
    >
      <ToastContainer position="top-right" autoClose={3000} theme={isDark ? "dark" : "light"} />

      <div style={{ padding: "20px 24px 48px" }}>
        <div style={{ marginBottom: 28 }}>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, display: "flex", alignItems: "center", gap: 10 }}>
            <FaFileCsv style={{ color: ACCENT }} />
            Loan Stage History Export
          </h1>
          <p style={{ margin: "6px 0 0", color: mutedTextColor, fontSize: 14 }}>
            Export raw stage change events for an organization as a CSV file.
          </p>
        </div>

        {orgSelectorSection}
        {exportSection}

        {!selectedOrganization && (
          <div style={{ textAlign: "center", padding: "48px 0", color: mutedTextColor }}>
            <FaBuilding style={{ fontSize: 42, opacity: 0.2, marginBottom: 14 }} />
            <p style={{ fontSize: 16, fontWeight: 600 }}>Select an organization to get started</p>
          </div>
        )}
      </div>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default LoanStageAnalyticsPage;
